import React from 'react';
import { FaCircle, FaTimes } from 'react-icons/fa';
import { GrDrag } from 'react-icons/gr';
import { Draggable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import { useUserPreferences } from 'App/contexts/UserPreferencesContext';
import useDeleteWorkQueueFilterMutation from '../hooks/useDeleteWorkQueueFilterMutation';
import { useFormikContext } from 'formik';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import { transformDbFilterToReactState } from '../workQueue.utils';
import { Button } from 'react-bootstrap';
import IconWrapper from 'App/common/styles/IconWrapper';
import PropTypes from 'prop-types';
import { FILTER_TYPE } from '../workQueue.constant';
import * as R from 'ramda';

const EXCLUDE_FILTER_KEYS = ['excludeBranches', 'excludeCategories', 'excludeActivityTypes', 'excludePatientLabels'];

const propTypes = {
    filter: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        uuid: PropTypes.string,
        filter: PropTypes.shape({}),
    }).isRequired,
    filterIndex: PropTypes.number,
    setShowDeleteFilterModal: PropTypes.func.isRequired,
    setDeleteFilter: PropTypes.func.isRequired,
};

const defaultProps = {
    filterIndex: null,
};

const Style = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    button {
        background: transparent;
        border: none;

        &.filter-record {
            flex-grow: 1;
        }

        &.remove-filter {
            color: var(--brandDanger);
        }

        &.active-filter.filter-record {
            background: var(--brandInfo);
            color: var(--snow);
            border-radius: 4px;

            .filter-name {
                color: var(--snow);

                &:hover {
                    color: var(--snow);
                }
            }
        }

        &.sort-alpha {
            padding-left: 0;
        }
    }
`;

const MyFilterItem = ({ filter, filterIndex, setShowDeleteFilterModal, setDeleteFilter }) => {
    const {
        values: {
            activeSavedFilter,
            filterType,
            allBranchesList,
            allCategoriesList,
            allActivityTypesList,
            allPatientLabelsList,
            allCustomOptionsList,
        },
        setFieldValue,
    } = useFormikContext();
    const isWorkQueueFiltersEnabled = useIsPermitted(PERMISSION_TYPES.ENABLE_WORK_QUEUE_FILTERS);
    const deleteWorkQueueFilterMutation = useDeleteWorkQueueFilterMutation();
    const { setPreference } = useUserPreferences();
    const deleteFilterHandler = (deleteFilter, e) => {
        e.stopPropagation();
        if (isWorkQueueFiltersEnabled) {
            setShowDeleteFilterModal(true);
            setDeleteFilter(deleteFilter);
        } else {
            setDeleteFilter(deleteFilter);
            deleteWorkQueueFilterMutation.mutateAsync(deleteFilter);
        }
    };

    const setFilter = (filterId) => {
        const modifiedFilter = isWorkQueueFiltersEnabled
            ? filter?.filter
            : R.omit([...EXCLUDE_FILTER_KEYS], filter?.filter);
        const getFilterParams = transformDbFilterToReactState(
            modifiedFilter,
            allBranchesList,
            allCategoriesList,
            allActivityTypesList,
            allPatientLabelsList,
            allCustomOptionsList
        );
        setFieldValue('filterParams', getFilterParams);
        if (isWorkQueueFiltersEnabled) {
            setFieldValue('activeSavedFilter', filterId);
            if (filterType === FILTER_TYPE.WORK_QUEUE) {
                setPreference('workQueue_activeSavedFilter', filterId);
            } else {
                setPreference('assignmentManager_activeSavedFilter', filterId);
            }
        }
    };

    return (
        <>
            {' '}
            {isWorkQueueFiltersEnabled ? (
                <Draggable draggableId={filter.name} index={filterIndex}>
                    {(providedDrag) => (
                        <Style
                            ref={providedDrag.innerRef}
                            {...providedDrag.draggableProps}
                            {...providedDrag.dragHandleProps}
                        >
                            <button
                                type="button"
                                className="rank-filter"
                                aria-label="Drag Filter"
                                data-testid={`rank-filter-${filterIndex}`}
                            >
                                <GrDrag />
                            </button>
                            <button
                                type="button"
                                aria-label={`Select ${filter.name}`}
                                className={`filter-record ${
                                    isWorkQueueFiltersEnabled && activeSavedFilter === filter.id ? 'active-filter' : ''
                                }`}
                                onClick={() => setFilter(filter.id)}
                            >
                                {!isWorkQueueFiltersEnabled && <FaCircle size={6} className="bullet" />}
                                <span className={isWorkQueueFiltersEnabled ? 'filter-name' : 'filter-text'}>
                                    {filter.name}
                                </span>
                            </button>
                            <button
                                type="button"
                                aria-label="Remove filter"
                                onClick={(e) => deleteFilterHandler(filter, e)}
                                className="remove-filter"
                            >
                                <FaTimes size={12} />
                            </button>
                        </Style>
                    )}
                </Draggable>
            ) : (
                <Button
                    aria-label={filter.name}
                    block
                    variant="link"
                    className="filter-record"
                    key={filter.id}
                    onClick={() => setFilter(filter.id)}
                >
                    <FaCircle size={6} className="bullet" />
                    <span className="filter-text">{filter.name}</span>
                    <IconWrapper>
                        <FaTimes
                            className="text-danger delete-filter"
                            size={12}
                            onClick={(e) => deleteFilterHandler(filter, e)}
                        />
                    </IconWrapper>
                </Button>
            )}{' '}
        </>
    );
};

MyFilterItem.propTypes = propTypes;
MyFilterItem.defaultProps = defaultProps;

export default MyFilterItem;
